import type { ReactNode } from 'react'

/**
 * П'ять переходів рушія верифікації (`SPEC-008` §5.4.1). Назва переходу — це
 * і ключ `_permissions` власника, і останній сегмент дієслівного роуту
 * `POST <restUrl>/{uuid}/<перехід>` (`SPEC-008` §6, `SPEC-009` §6.2).
 */
export type CrmVerificationTransition =
    | 'send_to_verification'
    | 'verify'
    | 'reject_verification'
    | 'blacklist'
    | 'unblacklist'

export const CRM_VERIFICATION_TRANSITIONS: readonly CrmVerificationTransition[] = [
    'send_to_verification',
    'verify',
    'reject_verification',
    'blacklist',
    'unblacklist',
]

/** Права переходів у `_permissions` картки власника. */
export type CrmVerificationPermissions = Partial<Record<CrmVerificationTransition | 'update', boolean>>

/** Порушення з 422 ядра (`ApiExceptionListener`): `field`, у легасі — `propertyPath`. */
export interface CrmViolation {
    field: string
    message: string
}

/**
 * Поля тіла `verify` (`SPEC-008` §5.4.3): експедитор, номер і дата договору.
 *
 * Експедитор договору зветься `contractForwarder`, а не `forwarder`
 * (`SPEC-009` Р9.4, нотатка куратора `SPEC-008`): `forwarder` — це вимога
 * № 13 до картки клієнта, і з однаковою назвою 422 тіла й 422 вимог не
 * розрізнити — «Має бути вказаний експедитор.» лягло б під заповнений
 * селект модалки.
 */
export const CRM_VERIFY_BODY_FIELDS = ['contractForwarder', 'contractNumber', 'contractDate'] as const

export type CrmVerifyBodyField = (typeof CRM_VERIFY_BODY_FIELDS)[number]

/**
 * Що власник (клієнт, згодом перевізник) дає спільним кнопкам верифікації.
 *
 * Тексти — свої в кожного власника: «Клієнта успішно верифіковано.» /
 * «Перевізника успішно верифіковано.» (`SPEC-008` §7.2, D-23). Підказку
 * модалки підтвердження власник передає готовим елементом: вартовий
 * унікальності `id` підказок читає лише літерал `<Hint id="…">`.
 */
export interface CrmVerificationOwnerConfig {
    /** Колекція власника: `/services/clients` */
    restUrl: string
    /** Підписи полів картки — для списку незаповнених вимог */
    fieldLabels: Readonly<Record<string, string>>
    /** Довідник юросіб для «Експедитора» в модалці підтвердження */
    forwarderResource: string
    texts: {
        /** «Верифікація клієнта» — заголовок модалки підтвердження */
        verifyModalTitle: string
        /** «Експедитор клієнта» */
        forwarderLabel: string
        /** «Додати клієнта до чорного списку?» */
        blacklistConfirmTitle: string
        /** Пояснення наслідку в діалозі чорного списку */
        blacklistConfirmText: string
        /** Тости успіху на кожен перехід */
        success: Record<CrmVerificationTransition, string>
    }
    /** Підказка в модалці підтвердження (`crm.<owner>.verify-contract`) */
    verifyHint?: ReactNode
}

/** Підписи кнопок (`SPEC-009` §8.7, `uk.json:392-396`) — спільні для власників. */
export const CRM_VERIFICATION_BUTTON_LABELS: Record<CrmVerificationTransition, string> = {
    send_to_verification: 'Відправити на верифікацію',
    verify: 'Підтвердити верифікацію',
    reject_verification: 'Відхилити верифікацію',
    blacklist: 'В чорний список',
    unblacklist: 'З чорного списку',
}

/** Тексти, спільні для власників (`SPEC-009` §8.6–§8.7). */
export const CRM_VERIFICATION_TEXTS = {
    successTitle: 'Успіх',
    errorTitle: 'Помилка',
    fillRequired: "Заповніть обов'язкові поля для верифікації.",
    contactAdmin: 'Зверніться до адміністратора системи.',
    rejectModalTitle: 'Вкажіть причину відхилення',
    contractNumber: 'Номер договору',
    contractDate: 'Дата договору',
    save: 'Зберегти',
    close: 'Закрити',
    /** Підпис «⋮» для читачів екрана: у самої кнопки лише іконка */
    actionsMenu: 'Дії верифікації',
} as const
